import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  CloseBoldIcon,
  CloseIcon,
  ExclamationTriangleIcon,
  ImageIcon,
  InfoCircleIcon,
  OverlayTriggerTooltip,
  RepromptIcon,
  SerifXIcon,
} from '@utilities';

import {
  createImageObject,
  IMAGE_UPLOAD_MAX_FILE_SIZE,
  uploadImageToS3,
} from '@api/instant_survey';
import { type Group } from '@api/instant_survey/utilities/types';

import styles from './_index.module.scss';

interface ConceptDetailsProps {
  group: Group;
  isReadOnly?: boolean;
  onUpdate: (data: Partial<Group>) => void;
}

interface ImageError {
  errorMsg: string;
}

const ConceptDetails = ({ group, isReadOnly, onUpdate }: ConceptDetailsProps) => {
  const [imageError, setImageError] = useState<ImageError | null>(null);
  const [isEditingVariable, setIsEditingVariable] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEditingVariable) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isEditingVariable &&
        inputWrapperRef.current &&
        e.target instanceof Node &&
        !inputWrapperRef.current.contains(e.target)
      ) {
        if (!group?.conceptName?.trim()) setIsEditingVariable(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingVariable, group?.conceptName]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > IMAGE_UPLOAD_MAX_FILE_SIZE) {
      setImageError({ errorMsg: 'File size exceeds 5MB limit' });
      return;
    }

    setImageError(null);
    setIsImageLoading(true);

    try {
      const imageObject = await createImageObject({ objectType: file.type.substring(6) });

      if (!imageObject.url.startsWith('http://s3mock')) {
        await uploadImageToS3({
          fields: imageObject.fields,
          file,
          url: imageObject.url,
        });
      }

      // Store a preview URL until the backend returns a cdnUrl
      onUpdate({ conceptImage: { id: imageObject.id, previewUrl: URL.createObjectURL(file) } });
    } catch (error) {
      setImageError({ errorMsg: 'please try again.' });
      console.error('Error uploading image:', error);
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleFileRemove = () => {
    onUpdate({ conceptImage: null });
  };

  const handleInputChange = (value: string) => {
    onUpdate({ conceptName: value });
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Use previewUrl if present, otherwise cdnUrl
  const imageUrl = group?.conceptImage?.cdnUrl || group?.conceptImage?.previewUrl;

  return (
    <div className={styles['concept-details']}>
      <h4 className={styles['concept-details-header']}>
        Concept Details
        <OverlayTriggerTooltip content="Respondents in this panelist group will see this concept image or concept name when used dynamically in the questionnaire.">
          <InfoCircleIcon />
        </OverlayTriggerTooltip>
      </h4>
      <div className={styles['concept-details-input-image-container']}>
        {!imageUrl && !isImageLoading && !imageError && !isReadOnly ? (
          <div
            aria-label="Add Image"
            className={styles['concept-details-image']}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick();
            }}
            role="button"
            tabIndex={0}
          >
            <ImageIcon />
            <p>
              Add a <strong>Concept Image</strong>
            </p>
            <input
              accept="image/gif, image/jpeg, image/png"
              className={styles['concept-details-input-file']}
              disabled={isImageLoading}
              hidden
              id="concept-image-input"
              onChange={handleFileChange}
              ref={fileInputRef}
              type="file"
            />
          </div>
        ) : (
          <>
            {imageError && (
              <div className={styles['concept-details-input-image-error']}>
                <div className={styles['concept-details-input-image-error-text']}>
                  <ExclamationTriangleIcon /> Image upload failed: {imageError.errorMsg}
                </div>
                <div
                  className={styles['concept-details-input-image-error-close-icon']}
                  onClick={() => setImageError(null)}
                >
                  <CloseBoldIcon />
                </div>
              </div>
            )}

            {isImageLoading && (
              <div className={styles['concept-details-input-image-loading']}>
                <RepromptIcon /> Image is uploading...
              </div>
            )}

            {!isImageLoading && !imageError && imageUrl && (
              <div className={styles['concept-details-input-image-preview']}>
                <img alt="Uploaded File" src={imageUrl} />
                {!isReadOnly && (
                  <div className={styles['concept-details-input-remove-image-container']}>
                    <OverlayTriggerTooltip content="Remove Image" placement="bottom">
                      <div
                        className={styles['concept-details-input-remove-image-button']}
                        onClick={handleFileRemove}
                      >
                        <CloseIcon className={styles['concept-details-input-remove-image-icon']} />
                      </div>
                    </OverlayTriggerTooltip>
                  </div>
                )}
                <div className={styles['concept-details-input-image-preview-overlay']} />
              </div>
            )}
          </>
        )}
      </div>

      <div className={styles['concept-details-variable']}>
        {isEditingVariable || isReadOnly || group?.conceptName ? (
          <div className={styles['concept-details-variable-input']} ref={inputWrapperRef}>
            <SerifXIcon />
            <textarea
              autoFocus
              className={styles['concept-details-input-field']}
              disabled={isReadOnly}
              id={`group-${group.clientId || group.id}-concept-name`}
              maxLength={128}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="Enter Name"
              value={group?.conceptName || ''}
            />
          </div>
        ) : (
          <div
            onClick={() => setIsEditingVariable(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setIsEditingVariable(true);
            }}
            role="button"
            tabIndex={0}
          >
            <SerifXIcon />
            <span>
              Add a <strong>Concept Name</strong>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConceptDetails;
